import { Amount } from 'src/app/types/Amount';
import { Business } from 'src/app/types/Business';
import { Profile } from 'src/app/types/Profile';
import { VendorBankingUiMode } from 'src/app/types/VendorUi';
import { resolveVendorBankingUiMode } from '../vendor-ui/vendor-ui-capability.util';

/** Invoice fields used to resolve per-partner withdrawal availability. */
export type InvoiceWithdrawalBalanceContext = {
  totalAmount?: Amount;
  dueAmount?: Amount;
  partnerCurrentBalance?: Amount;
};

function normalizeStatus(status?: string): string {
  return status?.trim().toUpperCase() ?? '';
}

/** True when at least one VBAN currency has bank details populated. */
export function hasNonEmptyVbanBankDetails(
  vbanBankDetailsByCurrency?: Profile['vbanBankDetailsByCurrency'],
): boolean {
  if (!vbanBankDetailsByCurrency) {
    return false;
  }

  return Object.values(vbanBankDetailsByCurrency).some(
    (details) => Array.isArray(details) && details.length > 0,
  );
}

/** Multi-currency or vendor VBAN is active (ACTIVATED flags from profile / settings). */
export function isVendorVbanOrMultiCurrencyActivated(
  profile: Profile | null | undefined,
): boolean {
  if (!profile) {
    return false;
  }

  if (profile.multiCurrencyActivated === true) {
    return true;
  }

  const multi = normalizeStatus(profile.multiCurrencyStatus);
  const vendor = normalizeStatus(profile.vendorVbanStatus);

  return multi === 'ACTIVATED' || vendor === 'ACTIVATED';
}

/**
 * When vendor VBAN or multi-currency is activated and VBAN bank details exist,
 * dashboard / withdrawal top balances use `walletBalancesByCurrency` (from API
 * `wallet_balance_list`), driven by selected currency — not client-sourced totals.
 */
export function shouldUseWalletBalanceListForTopLevel(
  profile: Profile | null | undefined,
): boolean {
  return (
    !!profile &&
    hasNonEmptyVbanBankDetails(profile.vbanBankDetailsByCurrency) &&
    isVendorVbanOrMultiCurrencyActivated(profile)
  );
}

/** Resolves displayed account balance for a currency given profile + client list rules. */
export function getTopLevelAccountBalanceForCurrency(
  businesses: Business[],
  currency: string,
  profile: Profile | null | undefined,
): number {
  const mode = resolveVendorBankingUiMode(profile);
  const normalizedCurrency = currency.toUpperCase();

  // UsdVbanActive & MultiCurrency: top card always uses vendor VBAN wallet_balance_list.
  if (
    (mode === VendorBankingUiMode.UsdVbanActive ||
      mode === VendorBankingUiMode.MultiCurrency) &&
    shouldUseWalletBalanceListForTopLevel(profile)
  ) {
    return getWalletBalanceForCurrency(
      profile?.walletBalancesByCurrency,
      normalizedCurrency,
    );
  }

  // LegacyWithVendorVban: USD dashboard balance is the sum of all client partner wallets,
  // not the vendor VBAN wallet (separate pools until multi-currency migration).
  if (
    mode === VendorBankingUiMode.LegacyWithVendorVban &&
    normalizedCurrency === 'USD'
  ) {
    return sumClientBalancesForCurrency(businesses, currency);
  }

  if (shouldUseWalletBalanceListForTopLevel(profile)) {
    return getWalletBalanceForCurrency(
      profile?.walletBalancesByCurrency,
      normalizedCurrency,
    );
  }

  return getAccountBalanceForCurrency(
    businesses,
    currency,
    profile?.walletBalancesByCurrency,
  );
}

export function normalizeInvoiceWithdrawalCurrency(
  invoice: InvoiceWithdrawalBalanceContext,
): string {
  const raw =
    invoice.totalAmount?.currency ??
    invoice.dueAmount?.currency ??
    invoice.partnerCurrentBalance?.currency ??
    'USD';
  return String(raw).trim().toUpperCase();
}

/** That invoice's (client) wallet balance for the given currency. */
export function getInvoicePartnerWalletBalanceForCurrency(
  invoice: InvoiceWithdrawalBalanceContext,
  currency: string,
): number {
  const normalizedCurrency = currency.toUpperCase();
  const balance = invoice.partnerCurrentBalance;
  if (!balance?.value || balance.value <= 0) {
    return 0;
  }

  const balanceCurrency = (balance.currency ?? 'USD').toUpperCase();
  if (balanceCurrency === normalizedCurrency) {
    return balance.value;
  }

  if (!balance.currency && normalizedCurrency === 'USD') {
    return balance.value;
  }

  return 0;
}

/**
 * Withdrawable amount for an invoice in invoice table. LegacyWithVendorVban: USD uses the
 * invoice partner's wallet; other currencies use the vendor wallet balance.
 */
export function getWithdrawalAvailabilityBalanceForInvoice(
  invoice: InvoiceWithdrawalBalanceContext,
  profile: Profile | null | undefined,
): number {
  const currency = normalizeInvoiceWithdrawalCurrency(invoice);

  if (
    resolveVendorBankingUiMode(profile) ===
    VendorBankingUiMode.LegacyWithVendorVban
  ) {
    if (currency === 'USD') {
      return getInvoicePartnerWalletBalanceForCurrency(invoice, currency);
    }
    return getWalletBalanceForCurrency(
      profile?.walletBalancesByCurrency,
      currency,
    );
  }

  if (!profile) {
    return getInvoicePartnerWalletBalanceForCurrency(invoice, currency);
  }

  if (shouldUseWalletBalanceListForTopLevel(profile)) {
    return getTopLevelAccountBalanceForCurrency([], currency, profile);
  }

  const resolved = getTopLevelAccountBalanceForCurrency([], currency, profile);
  if (resolved > 0) {
    return resolved;
  }

  return getInvoicePartnerWalletBalanceForCurrency(invoice, currency);
}

export function hasClientBankDetails(business: Business): boolean {
  return (business.bankDetails?.length ?? 0) > 0;
}

export function hasClientCurrentBalance(business: Business): boolean {
  return (business.currentBalance?.value ?? 0) > 0;
}

export function shouldUseVendorWalletBalance(businesses: Business[]): boolean {
  if (businesses.length === 0) {
    return true;
  }

  return businesses.every(
    (business) =>
      !hasClientBankDetails(business) && !hasClientCurrentBalance(business),
  );
}

export function sumClientBalancesForCurrency(
  businesses: Business[],
  currency: string,
): number {
  const normalizedCurrency = currency.toUpperCase();

  return businesses.reduce((total, business) => {
    const balance = business.currentBalance;
    if (!balance?.value) {
      return total;
    }

    const balanceCurrency = (balance.currency ?? 'USD').toUpperCase();
    if (balanceCurrency === normalizedCurrency) {
      return total + balance.value;
    }

    if (!balance.currency && normalizedCurrency === 'USD') {
      return total + balance.value;
    }

    return total;
  }, 0);
}

export function getWalletBalanceForCurrency(
  walletBalancesByCurrency: Record<string, number> | undefined,
  currency: string,
): number {
  if (!walletBalancesByCurrency) {
    return 0;
  }

  return walletBalancesByCurrency[currency.toUpperCase()] ?? 0;
}

export function getAccountBalanceForCurrency(
  businesses: Business[],
  currency: string,
  walletBalancesByCurrency?: Record<string, number>,
): number {
  if (shouldUseVendorWalletBalance(businesses)) {
    return getWalletBalanceForCurrency(walletBalancesByCurrency, currency);
  }

  return sumClientBalancesForCurrency(businesses, currency);
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  CAD: 'CA$',
  AUD: 'A$',
  SGD: 'S$',
  CHF: 'CHF ',
  TRY: '₺',
  AED: 'AED ',
  CNF: '¥',
  INR: '₹',
};

export function getCurrencySymbol(currencyCode: string): string {
  const code = (currencyCode || '').toUpperCase();
  if (!code) {
    return '';
  }
  return CURRENCY_SYMBOLS[code] ?? `${code} `;
}

export function buildWalletBalancesByCurrency(
  walletBalanceList: Array<{ currency?: string; amount?: number }> | undefined,
): Record<string, number> {
  const balances: Record<string, number> = {};

  (walletBalanceList ?? []).forEach((wallet) => {
    if (!wallet.currency) {
      return;
    }

    const code = wallet.currency.toUpperCase();
    balances[code] = (balances[code] ?? 0) + (wallet.amount ?? 0);
  });

  return balances;
}
